import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import CheckRoundedIcon   from '@mui/icons-material/CheckRounded';
import { useV4ColorMode } from '../theme';
import { UI_VARIANTS, variantPath } from '../../variants';

/** Two-tone dot: target variant's background with its accent color inside. */
export function VariantSwatch({ variant, mode, size = 18, round = true }) {
  const t = variant.tokens[mode];
  return (
    <Box sx={{
      width: size, height: size, flexShrink: 0,
      borderRadius: round ? '50%' : 0,
      background: t.bg,
      border: `${Math.max(2, size / 4)}px solid ${t.cyan}`,
      boxShadow: '0 0 0 1px rgba(var(--v4-fg-rgb),0.15)',
    }} />
  );
}

/**
 * Menu listing all UI variants; selecting one opens the current page in it.
 * `renderItem(variant, { active, index, mode })` lets each shell style the entries.
 */
export function ThemeMenu({ anchorEl, onClose, current, paperSx = {}, itemSx = {}, renderItem }) {
  const { mode } = useV4ColorMode();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const select = (v) => {
    onClose();
    if (v.id !== current) navigate(variantPath(v, pathname) + search);
  };

  return (
    <Menu
      anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { mt: 0.75, minWidth: 210, ...paperSx } }}
    >
      {UI_VARIANTS.map((v, index) => {
        const active = v.id === current;
        return (
          <MenuItem key={v.id} selected={active} onClick={() => select(v)} sx={itemSx}>
            {renderItem ? renderItem(v, { active, index, mode }) : (
              <>
                <VariantSwatch variant={v} mode={mode} />
                <Typography sx={{ flex: 1, ml: 1.5, fontSize: '0.88rem', fontWeight: active ? 700 : 500 }}>{v.label}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(var(--v4-muted-rgb),0.6)', ml: 2 }}>{v.id}</Typography>
                <CheckRoundedIcon sx={{ fontSize: 16, ml: 1, color: 'var(--v4-cyan)', visibility: active ? 'visible' : 'hidden' }} />
              </>
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
}

/** Palette icon button matching ColorModeToggle (v4 nav bar, v5 sidebar, sign-in). v6/v7/v3 use ThemeMenu with their own trigger. */
export default function ThemePicker({ sx = {} }) {
  const { variant } = useV4ColorMode();
  const [anchorEl, setAnchor] = useState(null);

  return (
    <>
      <Tooltip title="Theme wählen" placement="bottom">
        <IconButton
          size="small"
          onClick={e => setAnchor(e.currentTarget)}
          aria-label="Theme wählen"
          aria-haspopup="menu"
          sx={{
            color: 'var(--v4-cyan)',
            border: '1px solid rgba(var(--v4-cyan-rgb),0.15)',
            borderRadius: '10px',
            transition: 'all 0.2s ease',
            '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.08)' },
            '& svg': { transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)' },
            '&:hover svg': { transform: 'rotate(-20deg)' },
            ...sx,
          }}
        >
          <PaletteRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ThemeMenu
        anchorEl={anchorEl} onClose={() => setAnchor(null)} current={variant.id}
        paperSx={{ borderRadius: '14px', background: 'rgba(var(--v4-surface2-rgb),0.97)', border: '1px solid rgba(var(--v4-cyan-rgb),0.15)' }}
        itemSx={{ mx: 0.75, borderRadius: '10px', '&.Mui-selected': { background: 'rgba(var(--v4-cyan-rgb),0.12)' } }}
      />
    </>
  );
}
